import styled from 'styled-components';
import { BaseProductDTO, ProductDTO } from 'src/models/pim';
import {
  ProductAttributeCode,
  ProductTextValuesAttribute,
} from 'src/models/pim/attributes';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { Heading } from 'src/components/common/Heading';
import { media } from 'src/styles/designs';
import { SwiperSlide } from 'swiper/react';
import { ContentSwiper } from 'src/components/common/Swipers/ContentSwiper';
import { NavigationButton } from 'src/modules/materials/components/ShowcasePage/common';
import { head } from 'lodash';
import { useProductAttributes } from 'src/modules/products/hooks/useProductAttributes';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { useMemo } from 'react';
import { ProductPageInvolvedProductsItem } from 'src/modules/products/components/ProductPage/ProductPageInvolvedProducts/ProductPageInvolvedProductsItem';
import { StickyMenuSection } from 'src/components/common/StickyMenu/StickyMenuSection';
import { useTranslate } from 'react-t7e';

const Section = styled(StickyMenuSection)`
  padding: 32px 0;
  overflow: hidden;

  ${media.w.greaterThanOrEqual.desktop} {
    padding: 56px 0;
  }
`;

const SwiperContainer = styled(ContentSwiper)`
  justify-content: flex-start;

  &&& {
    ${NavigationButton} {
      top: 50%;
      transform: translateY(-50%);
      margin: 0;
      display: flex !important;
    }
  }

  .swiper-wrapper {
    gap: 20px;
  }

  .swiper-slide {
    width: min-content;
    height: auto;
    display: flex;
  }
`;

export const ProductPageInvolvedProducts = (product: ProductDTO) => {
  const translateFn = useTranslateItem();
  const { _ } = useTranslate();

  const { getProductAttribute, getMultipleProductAttributes } =
    useProductAttributes(product.attributes);

  const title = head(
    getProductAttribute(ProductAttributeCode.ProductsInvolvedTitle),
  );

  const items = useMemo(
    () =>
      getMultipleProductAttributes(ProductAttributeCode.ProductsInvolvedGroup)
        .map((attributes) => {
          const involvedProduct = translateFn(
            head(
              getProductAttribute(
                ProductAttributeCode.ProductsInvolvedGroupProduct,
                attributes,
              ),
            )?.products_id ?? ({} as BaseProductDTO),
          );

          return {
            ...involvedProduct,
            code:
              getProductAttribute(
                ProductAttributeCode.ProductsInvolvedGroupCode,
                attributes,
              ) ?? involvedProduct.code,
            name:
              translateFn(
                head(
                  getProductAttribute(
                    ProductAttributeCode.ProductsInvolvedGroupName,
                    attributes,
                  ),
                ) ?? ({} as ProductTextValuesAttribute['item']),
              )?.value ?? involvedProduct.name,
            image:
              head(
                getProductAttribute(
                  ProductAttributeCode.ProductsInvolvedGroupImage,
                  attributes,
                ),
              )?.directus_files_id ?? involvedProduct.image,
            description: translateFn(
              head(
                getProductAttribute(
                  ProductAttributeCode.ProductsInvolvedGroupText,
                  attributes,
                ),
              ) ?? ({} as ProductTextValuesAttribute['item']),
            )?.value,
          };
        })
        .filter((p) => !!p.code),
    [getMultipleProductAttributes, getProductAttribute, translateFn],
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <Section
      id="involved-products"
      label={_('Protocol products')}
      threshold={{
        desktop: 0.5,
        mobile: 0.8,
      }}
    >
      <ConstraintContainer>
        {title && (
          <Heading>
            <Markdown>{translateFn(title).value}</Markdown>
          </Heading>
        )}

        <SwiperContainer>
          {items.map((item, index) => (
            <SwiperSlide key={item.code + index}>
              <ProductPageInvolvedProductsItem {...item} />
            </SwiperSlide>
          ))}
        </SwiperContainer>
      </ConstraintContainer>
    </Section>
  );
};
